//
// # Schematic Instance Context Menu
//
// Displays instance information and actions when right-clicking on schematic instances.
// Selection-aware: operates on the full selection, not just the right-clicked instance.

import type { Editor } from "./editor";
import type { Instance } from "DrawCore/src/content/schematic/instance";
import { model } from "GenAlphaModel";
import type { schema } from "GenAlphaSchema";
import { InstanceBaseContextMenu } from "./instanceBaseContextMenu";
import { escapeHtml } from "./baseContextMenu";

export class SchematicInstanceContextMenu extends InstanceBaseContextMenu {
  // The specific instance under the mouse pointer (for single-instance info display)
  private currentInstance: Instance | null = null;

  constructor(editor: Editor) {
    super(editor);
  }

  // Show context menu for a schematic instance at the given position.
  // `selectionSize` indicates how many items are currently selected (for multi-select UI).
  show = (
    instance: Instance,
    pageX: number,
    pageY: number,
    selectionSize: number = 1
  ): void => {
    // Close any other open context menus first
    this.editor.hideAllContextMenus();

    this.currentInstance = instance;
    this.selectionSize = selectionSize;

    // Create or update menu element
    this.ensureMenuElement();

    // Build menu content
    const content = this.buildMenuContent(instance);

    this.displayMenu(content, pageX, pageY);

    // Set up click handlers for menu items
    this.setupInstanceMenuItemHandlers();
  };

  // Build the HTML content for the menu
  private buildMenuContent = (instance: Instance): string => {
    // Multi-select: show simplified header and only bulk actions
    if (this.selectionSize > 1) {
      return this.buildMultiSelectContent();
    }

    return this.buildSingleInstanceContent(instance);
  };

  // Build content for single-instance case (original behavior)
  private buildSingleInstanceContent = (instance: Instance): string => {
    const data = instance.data;
    const name = data.name;
    const target = data.target;
    const params = data.params;
    const instanceId = data.id;

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header">Instance: ${escapeHtml(
      name
    )}</div>`;

    // Helper to extract clean target name from Ident
    const getTargetDisplayName = (ident: typeof target): string => {
      if (ident.kind === "libcellview") {
        return `${ident.lib}.${ident.cell}`;
      } else if (ident.kind === "hierpath") {
        return ident.path.join(".");
      }
      return JSON.stringify(ident);
    };

    // Check if this is a parametrized instance (has params)
    const isParametrizedInstance = params && params.length > 0;

    // Show target
    if (target.kind === "libcellview") {
      content += `<div class="context-menu-target">Cell: ${target.lib}.${target.cell}</div>`;
    } else if (target.kind === "hierpath") {
      content += `<div class="context-menu-target">Cell: ${getTargetDisplayName(
        target
      )}</div>`;
    } else {
      content += `<div class="context-menu-target">Target: ${JSON.stringify(
        target
      )}</div>`;
    }

    // Show position
    content += `<div class="context-menu-position">Position: (${data.xform.x.toFixed(
      1
    )}, ${data.xform.y.toFixed(1)})</div>`;

    // Show parameters if this is a parametrized instance
    if (isParametrizedInstance) {
      content += `<div class="context-menu-section">`;
      content += `<div class="context-menu-section-header">Parameters:</div>`;
      params.forEach((param) => {
        const paramObj = model.schematic.InstanceParam.toObject(param);
        content += `<div class="context-menu-param">  ${paramObj.name}: ${paramObj.value}</div>`;
      });
      content += `</div>`;
    }

    content += `</div>`; // Close info section

    // Store the target ident for use in handlers
    // The target is schema.Ident (libcellview or hierpath)
    this.currentTargetIdent = target as schema.Ident;

    // Store the target symbol's linked schematic(s) for the "Open Schematic"
    // action. The resolved symbol is attached to the instance data, so its
    // forward `schematicImpls` references are available without a re-fetch.
    const symbol = instance.data.symbol;
    const schematicImpls = symbol?.schematicImpls;
    const hasSchematic = !!schematicImpls && schematicImpls.length > 0;
    this.currentSchematicImpls = hasSchematic ? schematicImpls : null;
    this.currentSymbolPdkId = symbol?.pdkId;

    // Add separator
    content += `<div class="context-menu-separator"></div>`;

    // Add action items
    content += `<div class="context-menu-actions">`;

    // Add "Open in New Tab" option - schematic instances always have a target
    content += `<div class="context-menu-item" data-action="open-target">Open in New Tab</div>`;
    // "Open Schematic" navigates to the schematic implementing this instance's
    // symbol. Disabled when the symbol has no linked schematic.
    const openSchematicClass = hasSchematic
      ? "context-menu-item"
      : "context-menu-item context-menu-item-disabled";
    content += `<div class="${openSchematicClass}" data-action="open-schematic">Open Schematic</div>`;
    content += `<div class="context-menu-item" data-action="view-in-depot">View in Depot</div>`;

    if (isParametrizedInstance && instanceId !== undefined) {
      content += `<div class="context-menu-item" data-action="edit-params" data-instance-id="${instanceId}">Edit Parameters...</div>`;
    }

    if (instanceId !== undefined) {
      content += `<div class="context-menu-item" data-action="rename" data-instance-id="${instanceId}">Rename...</div>`;
    }

    content += `<div class="context-menu-item" data-action="duplicate">Duplicate</div>`;
    content += `<div class="context-menu-item" data-action="delete">Delete</div>`;

    // Show in Hierarchy
    if (instanceId !== undefined) {
      content += `<div class="context-menu-separator"></div>`;
      content += `<div class="context-menu-item" data-action="show-in-hierarchy" data-instance-name="${escapeHtml(
        name
      )}" data-instance-id="${instanceId}">Show in Hierarchy</div>`;
    }

    content += `</div>`;

    return content;
  };

  // Null out instance-specific state (called by base clearState)
  protected clearInstanceState(): void {
    this.currentInstance = null;
  }
}
